import { useQueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { isDefined } from '../../../../utils/variables';
import { Button } from '../../button/button';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { InfoBox } from '../../info-box/info-box';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import type { PendingProjectTransferListItem } from './pending-project-transfer-types';
import { usePendingProjectTransfersQuery } from './use-pending-project-transfers-query';

export interface PendingProjectTransferRow extends Record<string, unknown> {
  token: string;
  companyId: string;
  projectName: string;
  targetCompanyName: string;
  acceptButton: React.ReactNode;
  declineButton: React.ReactNode;
}

function getColumns(t: TFunction): DataTableColumns<PendingProjectTransferRow> {
  return {
    targetCompanyName: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('pendingProjectTransfers.columns.targetCompany', { ns: 'companies' }),
      unfilterable: true,
    },
    projectName: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('pendingProjectTransfers.columns.project', { ns: 'companies' }),
      unfilterable: true,
    },
    acceptButton: {
      alignment: 'right',
      unfilterable: true,
      width: '100px',
    },
    declineButton: {
      alignment: 'right',
      unfilterable: true,
      width: '100px',
    },
  };
}

type PendingProjectTransfersProps = {
  showTitle?: boolean;
};

export const PendingProjectTransfers = ({ showTitle = true }: PendingProjectTransfersProps) => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const queryClient = useQueryClient();
  const [pendingToken, setPendingToken] = React.useState<string | null>(null);

  const { data, isLoading, error, isError } = usePendingProjectTransfersQuery();

  const respondToTransfer = React.useCallback(
    async (item: PendingProjectTransferListItem, decline: boolean) => {
      setPendingToken(item.token);
      try {
        await SkyMapAxiosServiceFactory.instance
          .createCompanyServiceV1()
          .respondToProjectTransferRequest({
            path: { companyId: item.companyId, token: item.token },
            body: { decline },
          });
        publishNotification(decline ? 'succ0314' : 'succ0313', {
          data: [item.transferProject?.name ?? '', item.targetCompany?.name ?? ''],
        });
        await queryClient.invalidateQueries({ queryKey: ['pending-project-transfers'] });
      } catch (err) {
        const code = (err as { response?: { data?: { notify?: { code?: string } } } })?.response
          ?.data?.notify?.code;
        if (isDefined(code)) {
          publishNotification(code);
        } else {
          publishNotification('err0001');
        }
      } finally {
        setPendingToken(null);
      }
    },
    [queryClient],
  );

  const onAccept = React.useCallback(
    (item: PendingProjectTransferListItem) => {
      void respondToTransfer(item, false);
    },
    [respondToTransfer],
  );

  const onDecline = React.useCallback(
    (item: PendingProjectTransferListItem) => {
      void respondToTransfer(item, true);
    },
    [respondToTransfer],
  );

  const columns = useRef(getColumns(t)).current;

  const pendingOnly = React.useMemo(() => {
    const all = data?.data ?? [];
    const pending = all.filter((i) => i.status === 'PENDING');
    return companyId ? pending.filter((i) => i.companyId === companyId) : pending;
  }, [data?.data, companyId]);

  const tableRows: PendingProjectTransferRow[] = React.useMemo(
    () =>
      pendingOnly.map((item) => ({
        token: item.token,
        companyId: item.companyId,
        projectName: item.transferProject?.name ?? '—',
        targetCompanyName: item.targetCompany?.name ?? '—',
        acceptButton: (
          <Button
            color="primary"
            disabled={pendingToken !== null}
            loading={pendingToken === item.token}
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              onAccept(item);
            }}
          >
            {t('pendingProjectTransfers.accept', { ns: 'companies' })}
          </Button>
        ),
        declineButton: (
          <Button
            color="primary"
            disabled={pendingToken !== null}
            loading={pendingToken === item.token}
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              onDecline(item);
            }}
          >
            {t('pendingProjectTransfers.decline', { ns: 'companies' })}
          </Button>
        ),
      })),
    [pendingOnly, pendingToken, onAccept, onDecline, t],
  );

  if (isError && error) {
    return (
      <Stack spacing={1}>
        {showTitle && <Title>{t('pendingProjectTransfers.title', { ns: 'companies' })}</Title>}
        <InfoBox color="red" leftIcon={{ icon: ['fad', 'circle-exclamation'] }}>
          {t('pendingProjectTransfers.loadError', { ns: 'companies' })}
        </InfoBox>
      </Stack>
    );
  }

  return (
    <>
      {showTitle && <Title>{t('pendingProjectTransfers.title', { ns: 'companies' })}</Title>}
      <OverlayLoader visible={isLoading}>
        {pendingOnly.length === 0 && !isLoading ? (
          <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'inbox'] }}>
            {t('pendingProjectTransfers.empty', { ns: 'companies' })}
          </InfoBox>
        ) : (
          <DataTable
            columns={columns}
            fixedLayout={false}
            items={tableRows}
            pageSize={15}
            tableStyle={{
              rowCell: { fontSize: '14px' },
              headerCell: { fontSize: '14px' },
            }}
          />
        )}
      </OverlayLoader>
    </>
  );
};

const Title = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;
